import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import periodService from '../../services/periodService';
import userService from '../../services/userService';

const PeriodForm = ({ open, onClose, period, onSuccess }) => {
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    administrator_id: '',
    start_date: '',
    end_date: '',
    initial_balance: '',
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      loadUsers();
      if (period) {
        // Modo edición
        setFormData({
          year: period.year || new Date().getFullYear(),
          administrator_id: period.administrator_id || '',
          start_date: period.start_date ? period.start_date.split('T')[0] : '',
          end_date: period.end_date ? period.end_date.split('T')[0] : '',
          initial_balance: period.initial_balance || '',
        });
      } else {
        // Modo creación - valores por defecto
        const currentYear = new Date().getFullYear();
        setFormData({
          year: currentYear,
          administrator_id: '',
          start_date: `${currentYear}-01-01`,
          end_date: `${currentYear}-12-31`,
          initial_balance: '0',
        });
      }
      setError('');
    }
  }, [period, open]);

  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      // Filtrar solo propietarios para seleccionar como admin temporal
      const owners = (response.users || []).filter(u => u.role === 'owner');
      setUsers(owners);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.year || formData.year < 2000 || formData.year > 2100) {
        setError('Año inválido');
        setLoading(false);
        return;
      }

      if (!formData.administrator_id) {
        setError('Debe seleccionar un administrador');
        setLoading(false);
        return;
      }

      if (!formData.start_date || !formData.end_date) {
        setError('Las fechas de inicio y fin son obligatorias');
        setLoading(false);
        return;
      }

      if (new Date(formData.start_date) >= new Date(formData.end_date)) {
        setError('La fecha de inicio debe ser anterior a la fecha de fin');
        setLoading(false);
        return;
      }

      if (!formData.initial_balance || parseFloat(formData.initial_balance) < 0) {
        setError('El balance inicial debe ser mayor o igual a 0');
        setLoading(false);
        return;
      }

      // Preparar datos
      const periodData = {
        year: parseInt(formData.year),
        administrator_id: formData.administrator_id,
        start_date: formData.start_date,
        end_date: formData.end_date,
        initial_balance: parseFloat(formData.initial_balance),
      };

      if (period) {
        // Actualizar período existente
        await periodService.update(period.id, periodData);
      } else {
        // Crear nuevo período
        await periodService.create(periodData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al guardar período';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  const getUserLabel = (user) => {
    if (user.house_number) {
      return `${user.name} - Casa ${user.house_number}`;
    }
    return user.name;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {period ? 'Editar Período' : 'Crear Nuevo Período'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 2, mt: 1 }}>
            <Alert severity="info">
              <Typography variant="body2">
                {period 
                  ? 'Solo puede editar períodos que no estén cerrados.'
                  : 'El administrador seleccionado recibirá el rol de "admin-temp" automáticamente.'}
              </Typography>
            </Alert>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Año"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                disabled={loading || (period && period.status === 'closed')}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Administrador Temporal"
                name="administrator_id"
                value={formData.administrator_id}
                onChange={handleChange}
                required
                disabled={loading}
                helperText="Nombre del administrador del período"
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Seleccione un propietario</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {getUserLabel(user)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Inicio"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de Fin"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleChange}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Balance Inicial (USD)"
                name="initial_balance"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={formData.initial_balance}
                onChange={handleChange}
                required
                disabled={loading}
                helperText="El balance con el que inicia este período administrativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading} size="large">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : (period ? 'Actualizar' : 'Crear Período')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PeriodForm;
